<template>
    <div class="pane-content">
        <el-row type="flex" class="align-center mt-2">
            <h5 class="section-header">Sync Settings</h5>
        </el-row>
        <salesforce-contact-settings
                ref="salesforceContactSettings"
                :model="model"
                :statics="staticName"
                v-if="integrationName === SALESFORCE_INTEGRATION"
        />

        <el-row type="flex" class="mt-2 align-center"
                v-if="CRMModuleWasChanged">
            <el-alert
                    class="mb-3"
                    type="warning"
                    :closable="false"
                    show-icon>
                <div class="d-block">
                    <div class="mb-2 text-black">
                        <template>
                            Please save the CRM module selection to enable all functionality.
                        </template>
                    </div>
                </div>
            </el-alert>
        </el-row>
        <div class="flex-column align-center"
             v-if="integrationName === HUBSPOT_INTEGRATION">
            <hubspot-sync-contact-mapper
                    :model="model"
                    :static-name="staticName"
                    :integration-name-with-casing="integrationNameWithCasing"
                    :default-mapper-settings="defaultMapperSettings"
                    class="mt-4"
            />
        </div>
      <div class="flex-column align-center"
           v-if="integrationName === GOHIGHLEVEL_INTEGRATION">
            <gohighlevel-sync-contact-mapper
                :model="model"
                :static-name="staticName"
                :integration-name-with-casing="integrationNameWithCasing"
                class="mt-4"
            />
      </div>

        <!-- End Synchronization -->
      <template v-if="GOHIGHLEVEL_INTEGRATION === integrationName">
        <el-row type="flex" :gutter="20" align="middle" class="mt-35">
          <el-col :span="15">
            <div class="flex-column">
              <el-row type="flex" class="mb-1 align-center">
                <b class="section-subheader">Aloware Line to send from <i class="text-danger">*</i></b>
              </el-row>
              <div class="flex-column">
                <span>
                  Select which Aloware line will be used to as Conversation Provider send messages to HighLevel contacts.
                </span>
              </div>
            </div>
          </el-col>
          <el-col :span="10">
            <div class="flex-column justify-center h-100">
              <el-tooltip class="item"
                          effect="dark"
                          placement="top"
                          :disabled="!! model.credentials.scopes && !! model.credentials.scopes.includes('conversations/message.write')">
                <div slot="content">
                  You need to re-authenticate the integration before you can start using this feature.
                </div>
                <el-select class="text-black"
                           placeholder="Select Line"
                           filterable
                           v-model="model.settings.filters.sms_campaign"
                           :disabled="! model.credentials.scopes || ! model.credentials.scopes.includes('conversations/message.write')"
                >
                  <el-option
                      v-for="item in listSmsCampaigns"
                      :key="item.id"
                      :label="item.name"
                      :value="item.id">
                  </el-option>
                </el-select>
              </el-tooltip>
            </div>
          </el-col>
        </el-row>
      </template>

        <!-- Start Push and Pull Contacts -->
        <el-row type="flex" class="mt-35 align-center"
                v-if="pushContactsIsAvailable || pullContactsIsAvailable">
            <h5 class="section-header">One-time Operations</h5>
        </el-row>
        <el-row v-if="pushContactsIsAvailable || pullContactsIsAvailable">
            <span v-if="integrationName !== SALESFORCE_INTEGRATION">
                {{ staticName }} automatically stays in sync with {{ integrationNameWithCasing }}, but in certain cases, you may need to do a one-time push and pull operation.
            </span>
            <span v-else>
              {{ staticName }} automatically stays in sync with {{ integrationNameWithCasing }}, but this feature is intended for emergency situations only.
          </span>
        </el-row>
        <el-row type="flex" :gutter="20" align="middle" class="mt-4"
                v-if="pushContactsIsAvailable || pullContactsIsAvailable">
            <el-col :span="15">
                <div class="flex-column">
                    <el-row v-if="pullContactsIsAvailable">
                        <b class="section-subheader">One-time Operations: {{ currentModulePluralAndFirstCapitalize }}</b>
                    </el-row>
                    <div class="flex-column">
                        <span v-if="pushContactsIsAvailable">
                            Use "Push {{ currentModulePluralAndFirstCapitalize }}" to see your {{ staticName }} contacts in {{ integrationNameWithCasing }}.
                        </span>
                        <span v-if="pullContactsIsAvailable">
                            Use "Pull {{ currentModulePluralAndFirstCapitalize }}" to see all your {{ integrationNameWithCasing }} contacts in {{ staticName }}.
                        </span>
                        <div class="sync-warning-note"
                             v-if="pullContactsIsAvailable">
                            <i class="el-icon-warning-outline mr-1"></i>
                            Pulling contacts will cause LRN lookup charges to be applied.
                        </div>
                    </div>
                </div>
            </el-col>
            <el-col :span="10">
                <div class="flex-column justify-center h-100">
                    <div class="flex-row gap-5 flex-wrap">
                        <el-button
                                @click="pushContacts"
                                :disabled="isPushingContacts || hubspotDisablePushAndPullContacts"
                                :loading="isPushingContacts"
                                v-if="pushContactsIsAvailable"
                                size="mini"
                                icon="el-icon-top"
                                class="flex-shrink-0"
                        >
                            Push {{ currentModulePluralAndFirstCapitalize }}
                        </el-button>
                        <el-button
                            @click="pullContacts"
                            :disabled="isPullingContacts || hubspotDisablePushAndPullContacts"
                            :loading="isPullingContacts"
                            v-if="pullContactsIsAvailable"
                            size="mini"
                            icon="el-icon-bottom"
                            class="flex-shrink-0"
                        >
                          Pull {{ currentModulePluralAndFirstCapitalize }}
                        </el-button>
                    </div>
                    <template v-if="lastSyncPullContactsList && lastSyncPullContactsList.length">
                        <div v-for="item in lastSyncPullContactsList" :key="item.module">
                            <small class="mt-1 sync-time-text">
                                Last pull {{ item.label }}: <b>{{ item.timestamp | fixFullDateUTCRelative }}</b>
                            </small>
                        </div>
                    </template>
                    <small v-if="pushContactsIsAvailable && model.settings?.last_sync_push_contact"
                           class="sync-time-text">
                        Last push {{ currentModulePluralAndFirstCapitalize }}: <b>{{ model.settings.last_sync_push_contact | fixFullDateUTCRelative }}</b>
                    </small>
                </div>
            </el-col>
        </el-row>
        <!-- End Push and Pull Contacts -->
        <!-- Start Pull Lead Statuses -->
        <el-row type="flex" :gutter="20" align="middle" class="mt-35"
                v-if="pullLeadStatusesIsAvailable">
            <el-col :span="15">
                <div class="flex-column">
                    <el-row type="flex" class="mb-1 align-center">
                        <b class="section-subheader">Pull Lead Statuses</b>
                    </el-row>
                    <div class="flex-column">
                        <span>
                            Pull your {{ integrationNameWithCasing }} lead statuses to {{ staticName }} as disposition statuses.
                        </span>
                    </div>
                </div>
            </el-col>
            <el-col :span="10">
                <div class="flex-column justify-center h-100">
                    <div>
                        <el-button
                                @click="pullLeadStatuses"
                                :disabled="isPullingLeadStatuses"
                                :loading="isPullingLeadStatuses"
                                size="mini"
                                icon="el-icon-bottom"
                        >
                            Pull Lead Statuses
                        </el-button>
                    </div>
                    <small v-if="model.settings?.last_sync_disposition_status" class="mt-2 sync-time-text">
                        Last disposition status sync:
                        <b>{{ model.settings.last_sync_disposition_status | fixFullDateUTCRelative }}</b>
                    </small>
                </div>
            </el-col>
        </el-row>
        <!-- End Pull Lead Statuses -->
        <!-- Start Pull Call Outcomes -->
        <el-row type="flex" :gutter="20" align="middle" class="mt-35"
                v-if="pullOutcomesIsAvailable">
            <el-col :span="15">
                <div class="flex-column">
                    <el-row type="flex" class="mb-1 align-center">
                        <b class="section-subheader">Pull Call Outcomes</b>
                    </el-row>
                    <div class="flex-column">
                        <span>
                            Pull your {{ integrationNameWithCasing }} call outcomes to {{ staticName }} as call disposition statuses.
                        </span>
                    </div>
                </div>
            </el-col>
            <el-col :span="10">
                <div class="flex-column justify-center h-100">
                    <div>
                        <el-button
                                @click="pullCallOutcomes"
                                :disabled="isPullingCallOutcomes"
                                :loading="isPullingCallOutcomes"
                                size="mini"
                                icon="el-icon-bottom"
                        >
                            Pull Call Outcomes
                        </el-button>
                    </div>
                    <small v-if="model.settings?.last_sync_call_disposition" class="mt-2 sync-time-text">
                        Last call outcomes sync:
                        <b>{{ model.settings.last_sync_call_disposition | fixFullDateUTCRelative }}</b>
                    </small>
                </div>
            </el-col>
        </el-row>
        <!-- End Pull Call Outcomes -->
        <!-- Start Pull Activity Types -->
        <el-row type="flex" :gutter="20" align="middle" class="mt-35"
                v-if="pullActivityTypesIsAvailable">
            <el-col :span="15">
                <div class="flex-column">
                    <el-row type="flex" class="mb-1 align-center">
                        <b class="section-subheader">Pull Activity Types</b>
                    </el-row>
                    <div class="flex-column">
                        <span>
                            Pull your {{ integrationNameWithCasing }} activity types to {{ staticName }} as activity types.
                        </span>
                    </div>
                </div>
            </el-col>
            <el-col :span="10">
                <div class="flex-column justify-center h-100">
                    <div>
                        <el-button
                                @click="pullActivityTypes"
                                :disabled="isPullingActivityTypes"
                                :loading="isPullingActivityTypes"
                                size="mini"
                                icon="el-icon-bottom"
                        >
                            Pull Activity Types
                        </el-button>
                    </div>
                    <small v-if="model.settings?.last_sync_activity_types" class="mt-2 sync-time-text">
                        Last activity types sync:
                        <b>{{ model.settings.last_sync_activity_types | fixFullDateUTCRelative }}</b>
                    </small>
                </div>
            </el-col>
        </el-row>
        <!-- End Pull Activity Types -->
        <!-- Start Pull Deals -->
        <el-row type="flex" :gutter="20" align="middle" class="mt-35"
                v-if="pullDealsIsAvailable">
            <el-col :span="15">
                <div class="flex-column">
                    <el-row type="flex" class="mb-1 align-center">
                        <b class="section-subheader">Pull Deals</b>
                    </el-row>
                    <div class="flex-column">
                        <span>
                            Pull your {{ integrationNameWithCasing }} Deals to {{ staticName }}.
                        </span>
                    </div>
                </div>
            </el-col>
            <el-col :span="10">
                <div class="flex-column justify-center h-100">
                    <div>
                        <el-button
                                @click="pullDeals"
                                :disabled="isPullingDeals"
                                :loading="isPullingDeals"
                                size="mini"
                                icon="el-icon-bottom"
                        >
                            Pull Deals
                        </el-button>
                    </div>
                    <small v-if="model.settings?.last_sync_deals" class="mt-2 sync-time-text">
                        Last deals sync: <b>{{ model.settings.last_sync_deals | fixFullDateUTCRelative }}</b>
                    </small>
                </div>
            </el-col>
        </el-row>
        <!-- End Pull Deals -->
        <!-- Start Pull Tickets -->
        <el-row type="flex" :gutter="20" align="middle" class="mt-35"
                v-if="pullTicketsIsAvailable">
            <el-col :span="15">
                <div class="flex-column">
                    <el-row type="flex" class="mb-1 align-center">
                        <b class="section-subheader">Pull Tickets</b>
                    </el-row>
                    <div class="flex-column">
                        <span>
                            Pull your {{ integrationNameWithCasing }} Tickets to {{ staticName }}.
                        </span>
                    </div>
                </div>
            </el-col>
            <el-col :span="10">
                <div class="flex-column justify-center h-100">
                    <div>
                        <el-button
                                @click="pullTickets"
                                :disabled="isPullingTickets"
                                :loading="isPullingTickets"
                                size="mini"
                                icon="el-icon-bottom"
                        >
                            Pull Tickets
                        </el-button>
                    </div>
                    <small v-if="model.settings?.last_sync_tickets" class="mt-2 sync-time-text">
                        Last tickets sync: <b>{{ model.settings.last_sync_tickets | fixFullDateUTCRelative }}</b>
                    </small>
                </div>
            </el-col>
        </el-row>
        <!-- End Pull Tickets -->
        <!-- Start Sync Users -->
        <el-row type="flex" :gutter="20" align="middle" class="mt-35"
                v-if="[SALESFORCE_INTEGRATION, GOHIGHLEVEL_INTEGRATION].includes(integrationName)">
          <el-col :span="15">
            <div class="flex-column">
              <el-row type="flex" class="mb-1 align-center">
                <b class="section-subheader">Sync Users</b>
                <el-tooltip
                  effect="dark"
                  placement="top">
                  <div slot="content">
                    Heads up! Users are synced by email, please make sure your agents have
                    <br/>
                    the same email address on {{ staticName }} and {{ integrationNameWithCasing }}.
                  </div>
                  <i class="el-icon-info ml-1"></i>
                </el-tooltip>
              </el-row>
              <div class="flex-column">
                          <span>
                              Connect your {{ integrationNameWithCasing }} to {{ staticName }} users.
                          </span>
                <span>
                              Only current users in {{ staticName }} are synced, and no new users will be created.
                          </span>
              </div>
            </div>
          </el-col>
          <el-col :span="10">
            <div class="flex-column justify-center h-100">
              <div>
                <el-tooltip class="item"
                            effect="dark"
                            placement="top"
                            :disabled="! reAuthenticateIsNeededToPullUsers">
                  <div slot="content">
                    You need to re-authenticate the integration before you can start using this feature.
                  </div>
                  <el-button
                      @click="pullUsers"
                      :disabled="isPullingUsers"
                      :loading="isPullingUsers"
                      size="mini"
                      icon="el-icon-refresh"
                  >
                    Sync Users
                  </el-button>
                </el-tooltip>
              </div>
              <small v-if="model.settings?.last_sync_user" class="mt-2 sync-time-text">
                Last user sync: <b>{{ model.settings.last_sync_user | fixFullDateUTCRelative }}</b>
              </small>
            </div>
          </el-col>
        </el-row>
        <!-- End Sync Users -->
    </div>
</template>
<script>
import {mapGetters, mapState} from 'vuex'
import HubspotSyncUsersMapper from './hubspot/sync-user-mapper.vue'
import HubspotSyncContactMapper from './hubspot/sync-contact-mapper.vue'
import {GOHIGHLEVEL_INTEGRATION, HUBSPOT_INTEGRATION, SALESFORCE_INTEGRATION} from "../../constants/integrations"
import {MODULE_ALL, MODULE_CONTACT, MODULE_LEAD, MODULE_ACCOUNT} from "../../constants/integration_modules"
import SalesforceContactSettings from "./salesforce/salesforce-contact-settings.vue"
import SyncContactMapper from "./gohighlevel/sync-contact-mapper.vue";

export default {
    components: {
        GohighlevelSyncContactMapper: SyncContactMapper,
        SalesforceContactSettings,
        HubspotSyncUsersMapper,
        HubspotSyncContactMapper,
    },
    props: {
        model: {
            type: Object,
            required: true,
        },
        integrationName: {
            type: String,
            required: true,
        },
        integrationNameWithCasing: {
            type: String,
            required: true
        },
        statics: {
            type: Object,
            required: true,
        },
        defaultMapperSettings: {
            type: Object,
            required: true,
        }
    },
    data: () => ({
        isPullingContacts: false,
        isPushingContacts: false,
        isPullingLeadStatuses: false,
        isPullingCallOutcomes: false,
        isPullingActivityTypes: false,
        isPullingUsers: false,
        isPullingDeals: false,
        isPullingTickets: false,
        HUBSPOT_INTEGRATION,
        SALESFORCE_INTEGRATION,
        GOHIGHLEVEL_INTEGRATION,
        module: MODULE_CONTACT,
        listSmsCampaigns: [],
    }),
  created() {
      if (this.integrationName === GOHIGHLEVEL_INTEGRATION) {
        this.getGoHighLevelSmsCampaigns()
        this.initializeSmsCampaign()
      }
  },
  computed: {
        ...mapState({
            currentCompany: state => state.cache.current_company
        }),
        ...mapGetters(['CRMModuleWasChanged']),
        hasFeatureVersion2() {
            return this.model?.credentials?.feature_version >= 2
        },
        staticName() {
            return this.statics.name || 'Aloware'
        },
        hubspotDisablePushAndPullContacts() {
            return [585, 1000].includes(this.currentCompany.id) && this.integrationName === HUBSPOT_INTEGRATION
        },
        reAuthenticateIsNeededToPullUsers() {
          return GOHIGHLEVEL_INTEGRATION === this.integrationName ? ! this.model?.credentials?.company_id : false
        },
        pushContactsIsAvailable() {
          switch (this.integrationName) {
            case HUBSPOT_INTEGRATION:
            case GOHIGHLEVEL_INTEGRATION:
              return true
            case SALESFORCE_INTEGRATION:
              return !this.CRMModuleWasChanged
            default:
              return false
          }
        },
        pullContactsIsAvailable() {
            switch (this.integrationName) {
                case HUBSPOT_INTEGRATION:
                case GOHIGHLEVEL_INTEGRATION:
                    return true
                case SALESFORCE_INTEGRATION:
                    return !this.CRMModuleWasChanged
                default:
                    return false
            }
        },
        pullLeadStatusesIsAvailable() {
            switch (this.integrationName) {
              case HUBSPOT_INTEGRATION:
                return true
              case SALESFORCE_INTEGRATION:
                return !this.CRMModuleWasChanged && this.model.settings?.contact_settings?.enable_lead
              default:
                return false
            }
        },
        pullOutcomesIsAvailable() {
            return this.integrationName === HUBSPOT_INTEGRATION
        },
        pullActivityTypesIsAvailable() {
            return this.integrationName === HUBSPOT_INTEGRATION
        },
        pullDealsIsAvailable() {
            return this.integrationName === HUBSPOT_INTEGRATION
        },
        pullTicketsIsAvailable() {
            return this.integrationName === HUBSPOT_INTEGRATION
        },
        currentModule() {
            if (this.integrationName === SALESFORCE_INTEGRATION) {
                if (!!this.model.settings?.contact_settings?.enable_contact) return MODULE_CONTACT
                if (!!this.model.settings?.contact_settings?.enable_lead) return MODULE_LEAD
                if (!!this.model.settings?.contact_settings?.enable_account) return MODULE_ACCOUNT
            }

            return MODULE_CONTACT
        },
        multipleModulesEnabled() {
            return this.integrationName === SALESFORCE_INTEGRATION
                    && this.hasFeatureVersion2
                    && [this.model.settings?.contact_settings?.enable_lead, this.model.settings?.contact_settings?.enable_contact, this.model.settings?.contact_settings?.enable_account].filter(Boolean).length >= 2
        },
        currentModulePluralAndFirstCapitalize() {
            if (this.multipleModulesEnabled) {
                const priorities = this.model.settings?.contact_settings?.priorities

                const enabled = {
                    [MODULE_LEAD]: !!this.model.settings?.contact_settings?.enable_lead,
                    [MODULE_CONTACT]: !!this.model.settings?.contact_settings?.enable_contact,
                    [MODULE_ACCOUNT]: !!this.model.settings?.contact_settings?.enable_account
                }

                // Helper to map module -> label
                const toLabel = (module_key) => {
                    switch (module_key) {
                        case MODULE_LEAD: return 'Leads'
                        case MODULE_CONTACT: return 'Contacts'
                        case MODULE_ACCOUNT: return 'Accounts'
                        default: return ''
                    }
                }

                if (Array.isArray(priorities) && priorities.length) {
                    const orderedEnabled = priorities.filter(p => enabled[p])
                    const labels = orderedEnabled.map(toLabel).filter(Boolean)
                    return labels.join(' / ')
                }

                // Fallback to legacy single priority (order lead/contact first by priority, then account)
                const legacyPriority = this.model.settings?.contact_settings?.priority
                const baseOrder = legacyPriority === MODULE_LEAD
                    ? [MODULE_LEAD, MODULE_CONTACT, MODULE_ACCOUNT]
                    : [MODULE_CONTACT, MODULE_LEAD, MODULE_ACCOUNT]

                const orderedEnabled = baseOrder.filter(p => enabled[p])
                const labels = orderedEnabled.map(toLabel).filter(Boolean)
                return labels.join(' / ')
            } else {
                return this.currentModule.charAt(0).toUpperCase() + this.currentModule.slice(1)
            }
        },
        lastSyncPullContactsList() {
            // Non-Salesforce stays as before (single value list)
            if (this.integrationName !== SALESFORCE_INTEGRATION) {
                const ts = this.integrationName === GOHIGHLEVEL_INTEGRATION
                    ? this.model.settings?.last_sync_contact
                    : this.model.settings?.last_sync_pull_contact
                return ts ? [{ module: 'default', label: this.currentModulePluralAndFirstCapitalize, timestamp: ts }] : []
            }

            const enabled = {
                [MODULE_LEAD]: !!this.model.settings?.contact_settings?.enable_lead,
                [MODULE_CONTACT]: !!this.model.settings?.contact_settings?.enable_contact,
                [MODULE_ACCOUNT]: !!this.model.settings?.contact_settings?.enable_account,
            }

            const timestamps = {
                [MODULE_LEAD]: this.model.settings?.last_sync_lead,
                [MODULE_CONTACT]: this.model.settings?.last_sync_contact,
                [MODULE_ACCOUNT]: this.model.settings?.last_sync_account,
                all: this.model.settings?.last_sync_all
            }

            const labels = {
                [MODULE_LEAD]: 'Leads',
                [MODULE_CONTACT]: 'Contacts',
                [MODULE_ACCOUNT]: 'Accounts'
            }

            const priorities = Array.isArray(this.model.settings?.contact_settings?.priorities)
                ? this.model.settings.contact_settings.priorities
                : []

            // Determine modules to show: enabled modules with their own timestamp; if none, fallback to last_sync_all
            const modulesOrdered = priorities.length
                ? priorities
                : [MODULE_LEAD, MODULE_CONTACT, MODULE_ACCOUNT]

            const list = []
            modulesOrdered.forEach(m => {
                if (enabled[m] && timestamps[m]) {
                    list.push({ module: m, label: labels[m], timestamp: timestamps[m] })
                }
            })

            if (!list.length && timestamps.all) {
                const enabledLabels = Object.keys(enabled)
                    .filter(k => enabled[k])
                    .map(k => labels[k])
                const label = enabledLabels.length ? enabledLabels.join(' / ') : 'All'
                list.push({ module: 'all', label, timestamp: timestamps.all })
            }

            return list
        }
    },
    methods: {
        getGoHighLevelSmsCampaigns() {
          axios.get('/api/v1/integration/gohighlevel/sms-campaigns').then((res) => {
            this.listSmsCampaigns = res.data
            console.log(res)
          }).catch((err) => {
            this.$root.handleErrors(err.response)
          })
        },
        initializeSmsCampaign() {
          // Initialize sms_campaign if not already set
          if (!this.model.settings?.filters?.sms_campaign) {
            this.$set(this.model.settings, 'filters', this.model.settings.filters || {})
            this.$set(this.model.settings.filters, 'sms_campaign', null)
          }
        },
        async pullContacts() {
            this.isPullingContacts = true
            try {
                await axios.post('/api/v1/integration/' + this.integrationName + '/pull-contacts')
                this.$notify({
                    offset: 95,
                    title: this.integrationNameWithCasing,
                    message: this.currentModulePluralAndFirstCapitalize + ' are being pulled from ' + this.integrationNameWithCasing + ', please wait for a few minutes for this process to finish. You will receive an email when we finish processing your request.',
                    type: 'success'
                })
            } catch (err) {
                this.$root.handleErrors(err.response)
            } finally {
                this.isPullingContacts = false
            }
        },
        async pushContacts() {
            this.isPushingContacts = true

            try {
                await axios.post('/api/v1/integration/' + this.integrationName + '/push-contacts')
                this.$notify({
                    offset: 95,
                    title: this.integrationNameWithCasing,
                    message: 'Contacts are being pushed to ' + this.integrationNameWithCasing + ', please wait for a few minutes for this process to finish. You will receive an email when we finish processing your request.',
                    type: 'success'
                })
            } catch (err) {
                this.$root.handleErrors(err.response)
            } finally {
                this.isPushingContacts = false
            }
        },
        async pullUsers() {
            this.isPullingUsers = true
            await new Promise(resolve => setTimeout(resolve, 1000))

            try {
                await axios.post('/api/v1/integration/' + this.integrationName + '/pull-users')
                this.$notify({
                    offset: 95,
                    title: this.integrationNameWithCasing,
                    message: 'Users are being synced with ' + this.integrationNameWithCasing + ', please wait for a few minutes for this process to finish. You will receive an email when we finish processing your request.',
                    type: 'success'
                })
            } catch (err) {
                this.$root.handleErrors(err.response)
            } finally {
                this.isPullingUsers = false
            }
        },
        async pullLeadStatuses() {
            this.isPullingLeadStatuses = true

            try {
                await axios.post('/api/v1/integration/' + this.integrationName + '/pull-lead-statuses')
                this.$notify({
                    offset: 95,
                    title: this.integrationNameWithCasing,
                    message: 'Lead status are being pulled from ' + this.integrationNameWithCasing + ', please wait for a few minutes for this process to finish. You will receive an email when we finish processing your request.',
                    type: 'success'
                })
            } catch (err) {
                this.$root.handleErrors(err.response)
            } finally {
                this.isPullingLeadStatuses = false
            }
        },
        async pullCallOutcomes() {
            this.isPullingCallOutcomes = true

            try {
                await axios.post('/api/v1/integration/' + this.integrationName + '/pull-call-dispositions')
                this.$notify({
                    offset: 95,
                    title: this.integrationNameWithCasing,
                    message: 'Call dispositions are being pulled from ' + this.integrationNameWithCasing + ', please wait for a few minutes for this process to finish. You will receive an email when we finish processing your request.',
                    type: 'success'
                })
            } catch (err) {
                this.$root.handleErrors(err.response)
            } finally {
                this.isPullingCallOutcomes = false
            }
        },
        async pullActivityTypes() {
            this.isPullingActivityTypes = true

            try {
                await axios.post('/api/v1/integration/' + this.integrationName + '/pull-activity-types')
                this.$notify({
                    offset: 95,
                    title: this.integrationNameWithCasing,
                    message: 'Activity types are being pulled from ' + this.integrationNameWithCasing + ', please wait for a few minutes for this process to finish. You will receive an email when we finish processing your request.',
                    type: 'success'
                })
            } catch (err) {
                this.$root.handleErrors(err.response)
            } finally {
                this.isPullingActivityTypes = false
            }
        },
        async pullDeals() {
            this.isPullingDeals = true

            try {
                await axios.post('/api/v1/integration/' + this.integrationName + '/pull-deals')
                this.$notify({
                    offset: 95,
                    title: this.integrationNameWithCasing,
                    message: 'Deals are being pulled from ' + this.integrationNameWithCasing + ', please wait for a few minutes for this process to finish. You will receive an email when we finish processing your request.',
                    type: 'success'
                })
            } catch (err) {
                this.$root.handleErrors(err.response)
            } finally {
                this.isPullingDeals = false
            }
        },
        async pullTickets() {
            this.isPullingTickets = true

            try {
                await axios.post('/api/v1/integration/' + this.integrationName + '/pull-tickets')
                this.$notify({
                    offset: 95,
                    title: this.integrationNameWithCasing,
                    message: 'Tickets are being pulled from ' + this.integrationNameWithCasing + ', please wait for a few minutes for this process to finish. You will receive an email when we finish processing your request.',
                    type: 'success'
                })
            } catch (err) {
                this.$root.handleErrors(err.response)
            } finally {
                this.isPullingTickets = false
            }
        }
    },
}
</script>

<style scoped>
.pane-content {
    display: flex;
    flex-direction: column;
    padding-left: 8px;
    padding-right: 8px;
}

.section-header {
    margin: 0 12px 0 0;
    font-size: 20px;
}

.section-subheader {
    font-size: 16px;
}

.align-center {
    align-items: center;
}

.flex-column {
    display: flex;
    flex-direction: column;
}

.flex-row {
    display: flex;
    flex-direction: row;
}

.justify-center {
    justify-content: center;
}

.h-100 {
    height: 100%;
}

.gap-5 {
    gap: 5px;
}

.mt-35 {
    margin-top: 35px;
}

.sync-time-text {
    font-size: 12px;
    color: #606266;
}

.sync-warning-note {
    display: inline-flex;
    align-items: center;
    background: #fffbe6;
    border: 1px solid #ffe58f;
    border-radius: 5px;
    padding: 10px 16px;
    color: #614700;
    margin-top: 5px;
    font-size: 14px;
    width: auto;
    max-width: max-content;
}
</style>
